import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';

const formatRequestCount = (value) => Number(value || 0).toLocaleString('en-US');
const formatDateCell = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', { timeZone: 'UTC' });
};
const formatDurationMs = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '—';
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(2)}s`;
  return `${numeric.toFixed(1)} ms`;
};
const formatErrorPercent = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return '—';
  if (numeric > 0 && numeric < 0.1) return '<0.1%';
  return `${numeric.toFixed(numeric < 1 ? 2 : 1)}%`;
};
const formatRejectionReason = (value) => {
  const labels = {
    historical_access_denied: 'Historical Access Denied',
    derived_data_access_denied: 'Derived Data Access Denied',
    datasource_access_denied: 'Data Source Access Denied',
    rate_limited: 'Rate Limited',
    invalid_query_params: 'Invalid Query Parameters',
    inactive_key: 'Inactive API Key',
    expired_key: 'Expired API Key',
    excel_plugin_access_denied: 'Excel Plugin Access Denied',
  };
  return labels[value] || value || 'Unknown';
};

const ErrorTrendChart = ({ data = [] }) => {
  if (!data.length) {
    return <div className="text-muted small">No error trend data</div>;
  }
  const width = 760;
  const height = 220;
  const leftPad = 52;
  const rightPad = 16;
  const topPad = 18;
  const bottomPad = 34;
  const chartWidth = width - leftPad - rightPad;
  const chartHeight = height - topPad - bottomPad;
  const maxValue = Math.max(...data.map((item) => Number(item.request_count || 0)), 1);
  const yMax = maxValue <= 5 ? 5 : Math.ceil(maxValue / 5) * 5;
  const yTicks = 5;
  const groupWidth = chartWidth / data.length;
  const barWidth = Math.max(8, Math.min(18, groupWidth - 4));

  return (
    <div className="border rounded-3 bg-white p-2">
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <svg
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block', minWidth: '640px', height: `${height}px` }}
          role="img"
          aria-label="daily endpoint error trend"
        >
          {Array.from({ length: yTicks + 1 }).map((_, index) => {
            const value = (yMax / yTicks) * index;
            const y = topPad + chartHeight - ((value / yMax) * chartHeight);
            return (
              <g key={`grid-${value}`}>
                <line x1={leftPad} y1={y} x2={width - rightPad} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                <text x={leftPad - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                  {Math.round(value)}
                </text>
              </g>
            );
          })}
          <line x1={leftPad} y1={topPad} x2={leftPad} y2={topPad + chartHeight} stroke="#94a3b8" strokeWidth="1" />
          <line x1={leftPad} y1={topPad + chartHeight} x2={width - rightPad} y2={topPad + chartHeight} stroke="#94a3b8" strokeWidth="1" />
          {data.map((item, index) => {
            const requestCount = Number(item.request_count || 0);
            const errorCount = Number(item.error_count || 0);
            const successCount = Math.max(0, requestCount - errorCount);
            const successHeight = (successCount / yMax) * chartHeight;
            const errorHeight = (errorCount / yMax) * chartHeight;
            const totalHeight = successHeight + errorHeight;
            const x = leftPad + (index * groupWidth) + ((groupWidth - barWidth) / 2);
            const label = new Date(item.usage_date).getUTCDate();
            return (
              <g key={`bar-${item.usage_date}`}>
                <rect
                  x={x}
                  y={topPad + chartHeight - totalHeight}
                  width={barWidth}
                  height={successHeight}
                  fill="#0f766e"
                  rx="2"
                />
                <rect
                  x={x}
                  y={topPad + chartHeight - errorHeight}
                  width={barWidth}
                  height={errorHeight}
                  fill="#b45309"
                  rx="2"
                />
                <text x={x + (barWidth / 2)} y={height - 10} textAnchor="middle" fontSize="10" fill="#6b7280">
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="d-flex align-items-center gap-3 px-2 pt-2 small text-muted">
        <span className="d-inline-flex align-items-center gap-1">
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: '#0f766e' }} />
          Success
        </span>
        <span className="d-inline-flex align-items-center gap-1">
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: '#b45309' }} />
          Errors
        </span>
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, first = false }) => (
  <div className={first ? 'rounded-3 border bg-light-subtle p-3 mb-3' : 'rounded-3 border bg-light-subtle p-3 mb-3 mt-4'}>
    <h6 className="fw-semibold mb-3 text-dark">{title}</h6>
    {children}
  </div>
);

const FiveColumnTableColGroup = () => (
  <colgroup>
    <col style={{ width: '34%' }} />
    <col style={{ width: '12%' }} />
    <col style={{ width: '18%' }} />
    <col style={{ width: '18%' }} />
    <col style={{ width: '18%' }} />
  </colgroup>
);

const OpsOrgApiEndpointAnalytics = () => {
  const { orgId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });

  const fetchAnalytics = async (mountedRef = { current: true }) => {
    setLoading(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/api-endpoint-analytics`);
      if (!mountedRef.current) return;
      setAnalytics(response.data);
    } catch (err) {
      if (!mountedRef.current) return;
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to load API endpoint analytics.' });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    const mountedRef = { current: true };
    if (orgId) fetchAnalytics(mountedRef);
    return () => {
      mountedRef.current = false;
    };
  }, [orgId]);

  const dailyErrorRows = useMemo(() => analytics?.daily_error_trend_month || [], [analytics]);
  const dailyErrorTableRows = useMemo(
    () => dailyErrorRows.filter((item) => Number(item.request_count || 0) > 0),
    [dailyErrorRows]
  );

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title mb-1">API Endpoint Analytics</h5>
        <div className="text-muted small mb-2">
          Current-month endpoint analytics for this organization. Metrics are near real time and may lag slightly behind live request activity.
        </div>
        {status.message && <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>}
        {loading ? (
          <div className="text-muted">Loading endpoint analytics...</div>
        ) : (
          <>
            <SectionCard title="Daily Error Trend" first>
              <ErrorTrendChart data={dailyErrorRows} />
              <div className="table-responsive mt-3">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th># Requests</th>
                      <th># Errors</th>
                      <th>Error Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyErrorTableRows.length === 0 ? (
                      <tr><td colSpan={4} className="text-muted">No daily error data recorded this month</td></tr>
                    ) : dailyErrorTableRows.map((item) => (
                      <tr key={item.usage_date}>
                        <td>{formatDateCell(item.usage_date)}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td>{formatRequestCount(item.error_count)}</td>
                        <td>{formatErrorPercent(item.error_rate_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Top Rejection Reasons">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Reason</th>
                      <th>Parameter Signature</th>
                      <th># Rejections</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.top_rejection_reasons_month || []).length === 0 ? (
                      <tr><td colSpan={3} className="text-muted">No tracked rejection reasons recorded this month</td></tr>
                    ) : analytics.top_rejection_reasons_month.map((item, index) => (
                      <tr key={`reason-${item.rejection_reason}-${item.parameter_signature || 'none'}-${index}`}>
                        <td>{formatRejectionReason(item.rejection_reason)}</td>
                        <td>{item.parameter_signature || '—'}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Rejections by Endpoint">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Reason</th>
                      <th>Parameter Signature</th>
                      <th># Rejections</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.rejection_by_endpoint_month || []).length === 0 ? (
                      <tr><td colSpan={4} className="text-muted">No endpoint-level rejections recorded this month</td></tr>
                    ) : analytics.rejection_by_endpoint_month.map((item, index) => (
                      <tr key={`rejection-endpoint-${item.route_pattern}-${item.rejection_reason}-${item.parameter_signature || 'none'}-${index}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{formatRejectionReason(item.rejection_reason)}</td>
                        <td>{item.parameter_signature || '—'}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Top Endpoints by Request Volume">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th># Requests</th>
                      <th>Avg Response Time</th>
                      <th>Error Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.top_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No endpoint usage recorded this month</td></tr>
                    ) : analytics.top_endpoints_month.map((item) => (
                      <tr key={`top-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td>{formatDurationMs(item.avg_duration_ms)}</td>
                        <td>{formatErrorPercent(item.rejected_rate_pct + item.server_error_rate_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Slowest Endpoints">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Avg Response Time</th>
                      <th>Max Response Time</th>
                      <th># Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.slowest_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No latency data with a sufficient sample size this month</td></tr>
                    ) : analytics.slowest_endpoints_month.map((item) => (
                      <tr key={`slow-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatDurationMs(item.avg_duration_ms)}</td>
                        <td>{formatDurationMs(item.max_duration_ms)}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Highest Rejected Request Rate Endpoints">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Rejected Rate</th>
                      <th># Requests</th>
                      <th>Rejection Mix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.highest_rejected_request_rate_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No rejected endpoint requests recorded this month</td></tr>
                    ) : analytics.highest_rejected_request_rate_endpoints_month.map((item) => (
                      <tr key={`rejected-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatErrorPercent(item.rejected_rate_pct)}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td className="small text-muted">
                          Client {formatRequestCount(item.client_error_count)} · Auth {formatRequestCount(item.auth_error_count)} · 429 {formatRequestCount(item.rate_limited_count)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Highest Server Error Rate Endpoints">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <FiveColumnTableColGroup />
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th>Server Error Rate</th>
                      <th># Requests</th>
                      <th>5xx Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.highest_server_error_rate_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={5} className="text-muted">No server endpoint errors recorded this month</td></tr>
                    ) : analytics.highest_server_error_rate_endpoints_month.map((item) => (
                      <tr key={`server-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatErrorPercent(item.server_error_rate_pct)}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td>{formatRequestCount(item.server_error_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard title="Request Outcome Breakdown by Endpoint">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Method</th>
                      <th># Requests</th>
                      <th>Success</th>
                      <th>Client</th>
                      <th>Auth</th>
                      <th>429</th>
                      <th>5xx</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.top_endpoints_month || []).length === 0 ? (
                      <tr><td colSpan={8} className="text-muted">No endpoint usage recorded this month</td></tr>
                    ) : analytics.top_endpoints_month.map((item) => (
                      <tr key={`breakdown-${item.http_method}-${item.route_pattern}`}>
                        <td className="text-break">{item.route_pattern}</td>
                        <td>{item.http_method}</td>
                        <td>{formatRequestCount(item.request_count)}</td>
                        <td>{formatRequestCount(item.success_count)}</td>
                        <td>{formatRequestCount(item.client_error_count)}</td>
                        <td>{formatRequestCount(item.auth_error_count)}</td>
                        <td>{formatRequestCount(item.rate_limited_count)}</td>
                        <td>{formatRequestCount(item.server_error_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </>
        )}
      </div>
    </div>
  );
};

export default OpsOrgApiEndpointAnalytics;
